import React, { useState, useEffect } from "react";
import { Card, Row, Col, Badge, Button, Tab, Tabs, Table, Alert } from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import PageTitle from "../../layouts/PageTitle";
import { 
  getDepositorByIdAction,
  getDepositorKycAction,
  getDepositHistoryAction,
  clearCurrentDepositor,
  clearErrors
} from "../../../store/actions/DepositorsActions";

const ViewDepositor = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { id } = useParams();

  const { 
    data: currentDepositor, 
    kycData,
    depositHistory,
    loading, 
    error 
  } = useSelector((state) => state.depositors);

  const [activeTab, setActiveTab] = useState('details');

  useEffect(() => {
    if (id) {
      dispatch(getDepositorByIdAction(id));
      dispatch(getDepositorKycAction(id));
      dispatch(getDepositHistoryAction(id));
    }

    return () => {
      dispatch(clearCurrentDepositor());
      dispatch(clearErrors());
    };
  }, [dispatch, id]);

  if (loading) {
    return (
      <div>
        <PageTitle activeMenu="View Depositor" motherMenu="Finance" />
        <Alert variant="info">Loading depositor information...</Alert>
      </div>
    );
  }

  if (error) {
    return (
      <div>
        <PageTitle activeMenu="View Depositor" motherMenu="Finance" />
        <Alert variant="danger">Error: {error}</Alert>
      </div>
    );
  }

  if (!currentDepositor || Object.keys(currentDepositor).length === 0) {
    return (
      <div>
        <PageTitle activeMenu="View Depositor" motherMenu="Finance" />
        <Alert variant="warning">Depositor not found.</Alert>
      </div>
    );
  }

  const getStatusBadgeVariant = (status) => {
    switch (status) {
      case 'Active': return 'success';
      case 'Pending': return 'warning';
      case 'Inactive': return 'danger';
      case 'Rejected': return 'danger';
      default: return 'light';
    }
  };

  const getKycProgress = () => {
    if (!kycData || kycData.length === 0) return 0;
    const completedCount = kycData.filter(kyc => kyc.isCompleted).length;
    return Math.round((completedCount / kycData.length) * 100);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR'
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '--';
    return new Date(dateString).toLocaleDateString('en-IN');
  };

  return (
    <div>
      <PageTitle activeMenu="View Depositor" motherMenu="Finance" />
      
      {/* Header */}
      <Row>
        <Col lg={12}>
          <Card>
            <Card.Header>
               <div className="d-flex justify-content-between align-items-center w-100">
                <div className="text-start">
                  <h4 className="card-title mb-1">{currentDepositor.name}</h4>
                </div>
                <div className="d-flex gap-2 ms-auto">
                  <Button 
                    variant="outline-primary" 
                    onClick={() => navigate(`/depositors/edit/${id}`)}
                  >
                    <i className="las la-edit me-2"></i>Edit
                  </Button>
                  <Button 
                    variant="outline-success" 
                    onClick={() => navigate(`/depositors/kyc/${id}`)}
                  >
                    <i className="las la-file-contract me-2"></i>Manage Documents
                  </Button>
                  <Button 
                    variant="secondary" 
                    onClick={() => navigate('/depositors')}
                  >
                    <i className="las la-arrow-left me-2"></i>Back
                  </Button>
                </div>
              </div>
            </Card.Header>
          </Card>
        </Col>
      </Row>

      {/* Tabs */}
      <Row>
        <Col lg={12}>
          <Card>
            <Card.Body>
              <Tabs
                activeKey={activeTab}
                onSelect={(k) => setActiveTab(k)}
                className="mb-4"
              >
                {/* Details Tab */}
                <Tab eventKey="details" title="Depositor Details">
                  <Row>
                    <Col md={6}>
                      <Card className="mb-4">
                        <Card.Header>
                          <h5 className="card-title">Personal Information</h5>
                        </Card.Header>
                        <Card.Body>
                          <table className="table table-borderless">
                            <tbody>
                              <tr>
                                <td className="fw-bold">Full Name:</td>
                                <td>{currentDepositor.name}</td>
                              </tr>
                              <tr>
                                <td className="fw-bold">Email:</td>
                                <td>{currentDepositor.email}</td>
                              </tr>
                              <tr>
                                <td className="fw-bold">Phone Number:</td>
                                <td>{currentDepositor.phoneNo}</td>
                              </tr>
                              <tr>
                                <td className="fw-bold">Address:</td>
                                <td>{currentDepositor.address || '--'}</td>
                              </tr>
                              <tr>
                                <td className="fw-bold">City:</td>
                                <td>{currentDepositor.city || '--'}</td>
                              </tr>
                              <tr>
                                <td className="fw-bold">District:</td>
                                <td>{currentDepositor.district || '--'}</td>
                              </tr>
                              <tr>
                                <td className="fw-bold">Status:</td>
                                <td>
                                  <Badge bg={getStatusBadgeVariant(currentDepositor.status)}>
                                    {currentDepositor.status}
                                  </Badge>
                                </td>
                              </tr>
                            </tbody>
                          </table>
                        </Card.Body>
                      </Card>
                    </Col>

                    <Col md={6}>
                      <Card className="mb-4">
                        <Card.Header>
                          <h5 className="card-title">Company & Branch</h5>
                        </Card.Header>
                        <Card.Body>
                          <table className="table table-borderless">
                            <tbody>
                              {/* <tr>
                                <td className="fw-bold">Company Code:</td>
                                <td>{currentDepositor.companyCode || '--'}</td>
                              </tr> */}
                              <tr>
                                <td className="fw-bold">Company:</td>
                                <td>{currentDepositor.companyName || '--'}</td>
                              </tr>
                              <tr>
                                <td className="fw-bold">Created Date:</td>
                                <td>{formatDate(currentDepositor.createdAt)}</td>
                              </tr>
                              <tr>
                                <td className="fw-bold">Last Updated:</td>
                                <td>{formatDate(currentDepositor.updatedAt)}</td>
                              </tr>
                              <tr>
                                <td className="fw-bold">Created By:</td>
                                <td>{currentDepositor.createdByName || '--'}</td>
                              </tr>
                              <tr>
                                <td className="fw-bold">Updated By:</td>
                                <td>{currentDepositor.updatedByName || '--'}</td>
                              </tr>
                            </tbody>
                          </table>
                        </Card.Body>
                      </Card>
                    </Col>
                  </Row>

                  {currentDepositor.notes && (
                    <Row>
                      <Col lg={12}>
                        <Card>
                          <Card.Header>
                            <h5 className="card-title">Notes</h5>
                          </Card.Header>
                          <Card.Body>
                            <p className="mb-0">{currentDepositor.notes}</p>
                          </Card.Body>
                        </Card>
                      </Col>
                    </Row>
                  )}
                </Tab>

                {/* KYC Tab */}
                <Tab eventKey="kyc" title={`KYC Status (${getKycProgress()}%)`}>
                  <Row>
                    <Col lg={12}>
                      <div className="d-flex justify-content-between align-items-center mb-4">
                        <h5>Documents Status</h5>
                        <Button 
                          variant="primary" 
                          onClick={() => navigate(`/depositors/kyc/${id}`)}
                        >
                          <i className="las la-edit me-2"></i>Manage Documents
                        </Button>
                      </div>
                      
                      {kycData && kycData.length > 0 ? (
                        <div className="table-responsive">
                          <Table striped bordered hover>
                            <thead>
                              <tr>
                                <th>#</th>
                                <th>Document Type</th>
                                <th>Status</th>
                                <th>Submitted Value</th>
                                <th>Submitted Date</th>
                              </tr>
                            </thead>
                            <tbody>
                              {kycData.map((kyc, index) => (
                                <tr key={kyc.id || index}>
                                  <td>{index + 1}</td>
                                  <td>
                                    <div className="fw-bold">{kyc.documentType}</div>
                                    {kyc.isRequired && (
                                      <small className="text-danger">Required</small>
                                    )}
                                  </td>
                                  <td>
                                    <Badge bg={kyc.isCompleted ? 'success' : 'warning'}>
                                      {kyc.isCompleted ? 'Completed' : 'Pending'}
                                    </Badge>
                                  </td>
                                  <td>
                                    {kyc.isCompleted ? (
                                      <span className="text-success">
                                        {kyc.submittedValue || 'Submitted'}
                                      </span>
                                    ) : (
                                      <span className="text-muted">Not submitted</span>
                                    )}
                                  </td>
                                  <td>
                                    {kyc.submittedDate ? formatDate(kyc.submittedDate) : '--'}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </Table>
                        </div>
                      ) : (
                        <Alert variant="info">
                          <i className="las la-info-circle me-2"></i>
                          No KYC requirements found for this depositor.
                        </Alert>
                      )}
                    </Col>
                  </Row>
                </Tab>

                {/* Transaction History Tab */}
                <Tab eventKey="history" title="Transaction History">
                  <Row>
                    <Col lg={12}>
                      <h5 className="mb-4">Deposit History</h5>
                      
                      {depositHistory && depositHistory.length > 0 ? (
                        <div className="table-responsive">
                          <Table striped bordered hover>
                            <thead>
                              <tr>
                                <th>#</th>
                                <th>Transaction Type</th>
                                <th>Amount</th>
                                <th>Date</th>
                                <th>Description</th>
                                <th>Balance</th>
                              </tr>
                            </thead>
                            <tbody>
                              {depositHistory.map((transaction, index) => (
                                <tr key={transaction.id || index}>
                                  <td>{index + 1}</td>
                                  <td>
                                    <Badge bg={transaction.type === 'CREDIT' ? 'success' : 'danger'}>
                                      {transaction.type}
                                    </Badge>
                                  </td>
                                  <td className={transaction.type === 'CREDIT' ? 'text-success' : 'text-danger'}>
                                    {formatCurrency(transaction.amount)}
                                  </td>
                                  <td>{formatDate(transaction.transactionDate)}</td>
                                  <td>{transaction.description || '--'}</td>
                                  <td className="fw-bold">
                                    {formatCurrency(transaction.balance)}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </Table>
                        </div>
                      ) : (
                        <Alert variant="info">
                          <i className="las la-info-circle me-2"></i>
                          No transaction history found for this depositor.
                        </Alert>
                      )}
                    </Col>
                  </Row>
                </Tab>
              </Tabs>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default ViewDepositor;